import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../lib/brand";
import { useEscape } from "../lib/ui/overlay";
import { services } from "../services";
import { useTheme } from "./useTheme";
import type { Command } from "../types";

const ROUTE_BY_COMMAND: Record<string, string> = {
  "new-song": "/generate",
  "go-home": "/",
  "go-generate": "/generate",
  "go-library": "/library",
  "go-renders": "/renders",
  "go-channels": "/channels",
  "go-settings": "/settings",
};

/** ⌘K command palette. Opens via ⌘K/Ctrl+K or the `voc:cmdk` window event. */
export function CommandPalette() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toggle: toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [commands, setCommands] = useState<Command[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEscape(open, () => setOpen(false));

  useEffect(() => {
    void services.commands.list().then(setCommands);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    const onEvent = () => setOpen(true);
    window.addEventListener("keydown", onKey);
    window.addEventListener("voc:cmdk", onEvent as EventListener);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("voc:cmdk", onEvent as EventListener);
    };
  }, []);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  const filtered = useMemo(
    () => commands.filter((c) => c.label.toLowerCase().includes(query.toLowerCase())),
    [commands, query],
  );

  const run = (cmd: Command) => {
    setOpen(false);
    if (cmd.id === "toggle-theme") return toggleTheme();
    const route = ROUTE_BY_COMMAND[cmd.id];
    if (route) navigate(route);
  };

  if (!open) return null;

  const groups = filtered.reduce<Record<string, Command[]>>((acc, c) => {
    (acc[c.group] ??= []).push(c);
    return acc;
  }, {});
  let flatIndex = -1;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("shell.search_placeholder")}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1200,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        paddingTop: "12vh",
        background: "rgba(10,10,11,0.6)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 560,
          margin: "0 16px",
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          boxShadow: "0 24px 64px -24px rgba(0,0,0,.6)",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 16px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <Icon name="search" size={16} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown") {
                e.preventDefault();
                setActive((a) => Math.min(a + 1, filtered.length - 1));
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setActive((a) => Math.max(a - 1, 0));
              } else if (e.key === "Enter") {
                const cmd = filtered[active];
                if (cmd) run(cmd);
              }
            }}
            placeholder={t("cmdk.placeholder")}
            aria-label={t("cmdk.placeholder")}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "var(--fg)",
              fontSize: 15,
            }}
          />
          <kbd
            style={{
              fontFamily: "Geist Mono, monospace",
              fontSize: 11,
              color: "var(--fg-subtle)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "1px 6px",
            }}
          >
            esc
          </kbd>
        </div>
        <div role="listbox" style={{ maxHeight: "50vh", overflowY: "auto", padding: 8 }}>
          {filtered.length === 0 ? (
            <p style={{ padding: 20, textAlign: "center", color: "var(--fg-muted)", fontSize: 13 }}>
              {t("cmdk.empty")}
            </p>
          ) : (
            Object.entries(groups).map(([group, items]) => (
              <div key={group} style={{ marginBottom: 8 }}>
                <div
                  style={{
                    fontSize: 10,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    color: "var(--fg-subtle)",
                    padding: "8px 10px 4px",
                  }}
                >
                  {group}
                </div>
                {items.map((cmd) => {
                  flatIndex += 1;
                  const isActive = flatIndex === active;
                  return (
                    <button
                      key={cmd.id}
                      role="option"
                      aria-selected={isActive}
                      onMouseEnter={() => setActive(filtered.indexOf(cmd))}
                      onClick={() => run(cmd)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: "100%",
                        padding: "10px",
                        borderRadius: 6,
                        border: "none",
                        background: isActive ? "var(--bg-hover)" : "transparent",
                        color: "var(--fg)",
                        fontSize: 13,
                        textAlign: "left",
                        cursor: "pointer",
                      }}
                    >
                      <Icon name={cmd.icon as never} size={16} />
                      {cmd.label}
                      {cmd.hint && (
                        <span
                          style={{
                            marginLeft: "auto",
                            fontFamily: "Geist Mono, monospace",
                            fontSize: 11,
                            color: "var(--fg-subtle)",
                          }}
                        >
                          {cmd.hint}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
